import { useEffect, useState } from 'react';
import { api, balanceLabel, dateFmt, money, STATUS_RU, todayIso } from '../api';
import { useAuth } from '../auth';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, useToast } from '../ui';
import { ClientLogin } from './ClientLogin';

type Summary = {
  client: { id: string; code: string; name: string; phone: string; type: string };
  balance: { balanceKop: string; isDebt: boolean; bottlesOnHands: number };
  credit: { allowed: boolean; reason: string | null; availableKop: string };
  addresses: Array<{ id: string; street: string; house: string; flat: string | null }>;
  nextOrder: { number: string; deliveryDate: string; totalKop: string; items: Array<{ name: string; qty: number }> } | null;
  lastDelivery: { createdAt: string; givenFull: number; takenEmpty: number; order: { number: string } } | null;
};

type Product = { id: string; name: string; priceKop: string; imageUrl: string | null; kind: string };

/**
 * Кабинет клиента.
 *
 * Цель экрана — снять с оператора звонки «сколько я должен» и «привезите
 * завтра две». Поэтому баланс и кнопка заказа видны сразу, без переходов,
 * а вход не спрашивает код чаще раза в квартал.
 */
export function ClientApp() {
  const { me, loading } = useAuth();

  if (loading) return <Loading />;
  if (!me || me.scope !== 'client') return <ClientLogin />;
  return <Cabinet />;
}

function Cabinet() {
  const { logout } = useAuth();
  const { data, error, loading, reload } = useApi<Summary>('/me/summary');
  const products = useApi<Product[]>('/products?kind=WATER_19L');
  const orders = useApi<Array<{ id: string; number: string; status: string; deliveryDate: string; totalKop: string }>>('/me/orders?limit=10');
  const [tab, setTab] = useState<'main' | 'order' | 'history'>('main');
  const toast = useToast();

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const b = balanceLabel(data.balance.balanceKop);

  return (
    <div className="mobile">
      <header>
        <img src="/logo.png" alt="" />
        <button className="ghost" style={{ color: '#fff', fontSize: 12 }} onClick={logout}>
          выйти
        </button>
      </header>

      <div className="body">
        <div className="tabs">
          <button className={tab === 'main' ? 'active' : ''} onClick={() => setTab('main')}>
            Мой счёт
          </button>
          <button className={tab === 'order' ? 'active' : ''} onClick={() => setTab('order')}>
            Заказать
          </button>
          <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
            История
          </button>
        </div>

        {tab === 'main' ? (
          <>
            <div className="card">
              <div className="muted">Здравствуйте, {data.client.name}</div>
              <div
                style={{ fontSize: 28, fontWeight: 700, margin: '6px 0' }}
                className={b.kind === 'debt' ? 'debt' : b.kind === 'prepaid' ? 'prepaid' : ''}
              >
                {b.text}
              </div>
              <div className="spread" style={{ marginTop: 10 }}>
                <span>Бутылей у вас на руках</span>
                <b style={{ fontSize: 20 }}>{data.balance.bottlesOnHands}</b>
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Пустую тару водитель забирает при следующей доставке.
              </div>
              {data.balance.isDebt ? (
                <button
                  className="primary big"
                  style={{ width: '100%', marginTop: 12 }}
                  onClick={async () => {
                    const res = await api<{ message: string }>('/me/payments/link', {
                      body: { amountKop: String(-BigInt(data.balance.balanceKop)) },
                    });
                    toast.show(res.message, 5000);
                  }}
                >
                  Оплатить {money(-BigInt(data.balance.balanceKop))}
                </button>
              ) : null}
            </div>

            {data.nextOrder ? (
              <div className="card">
                <h3>Ближайшая доставка</h3>
                <div className="spread">
                  <span>{dateFmt(data.nextOrder.deliveryDate)}</span>
                  <b>{money(data.nextOrder.totalKop)}</b>
                </div>
                <div className="muted">
                  {data.nextOrder.items.map((i) => `${i.name} × ${i.qty}`).join(', ')}
                </div>
              </div>
            ) : (
              <div className="card">
                <h3>Доставка не запланирована</h3>
                <button className="primary big" style={{ width: '100%' }} onClick={() => setTab('order')}>
                  Заказать воду
                </button>
              </div>
            )}

            <div className="card">
              <h3>Отсрочка оплаты</h3>
              {data.credit.allowed ? (
                <div className="ok">Можно заказать в долг, доступно {money(data.credit.availableKop)}</div>
              ) : (
                <div className="muted">{data.credit.reason}</div>
              )}
            </div>

            {data.lastDelivery ? (
              <div className="card">
                <h3>Последняя доставка</h3>
                <div className="muted">
                  {dateFmt(data.lastDelivery.createdAt)} · привезли {data.lastDelivery.givenFull}, забрали{' '}
                  {data.lastDelivery.takenEmpty}
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        {tab === 'order' ? (
          <NewOrder
            products={products.data ?? []}
            addressId={data.addresses[0]?.id}
            creditAllowed={data.credit.allowed}
            onDone={async () => {
              toast.show('Заказ принят');
              setTab('main');
              await reload();
              await orders.reload();
            }}
          />
        ) : null}

        {tab === 'history' ? (
          <div className="card">
            {!orders.data?.length ? (
              <Empty>Заказов пока не было</Empty>
            ) : (
              <table>
                <tbody>
                  {orders.data.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <div className="mono">{o.number}</div>
                        <div className="muted">{dateFmt(o.deliveryDate)}</div>
                      </td>
                      <td className="muted">{STATUS_RU[o.status] ?? o.status}</td>
                      <td className="num">{money(o.totalKop)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}
      </div>
      {toast.node}
    </div>
  );
}

function NewOrder({
  products,
  addressId,
  creditAllowed,
  onDone,
}: {
  products: Product[];
  addressId?: string;
  creditAllowed: boolean;
  onDone: () => Promise<void>;
}) {
  const [cart, setCart] = useState<Record<string, number>>({});
  const [date, setDate] = useState(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  });
  const [method, setMethod] = useState('CASH');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (products.length && !Object.keys(cart).length) setCart({ [products[0]!.id]: 2 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const total = products.reduce((s, p) => s + BigInt(p.priceKop) * BigInt(cart[p.id] ?? 0), 0n);
  const items = Object.entries(cart)
    .filter(([, q]) => q > 0)
    .map(([productId, qty]) => ({ productId, qty }));

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!items.length) return;
        setBusy(true);
        setError(null);
        try {
          await api('/me/orders', {
            body: {
              addressId,
              deliveryDate: date,
              items,
              paymentMethod: method,
              // Ключ создаётся на устройстве: двойное нажатие «Заказать»
              // не должно превращаться в два заказа
              idempotencyKey: `web-${date}-${JSON.stringify(items)}`,
            },
          });
          setCart({});
          await onDone();
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="card">
        <h3>Что привезти</h3>
        {products.map((p) => (
          <div className="product" key={p.id}>
            {p.imageUrl ? <img src={p.imageUrl} alt="" /> : null}
            <div style={{ flex: 1 }}>
              <div className="name">{p.name}</div>
              <div className="muted">{money(p.priceKop)}</div>
            </div>
            <div className="counter">
              <button type="button" onClick={() => setCart({ ...cart, [p.id]: Math.max(0, (cart[p.id] ?? 0) - 1) })}>
                −
              </button>
              <div className="val">{cart[p.id] ?? 0}</div>
              <button type="button" onClick={() => setCart({ ...cart, [p.id]: (cart[p.id] ?? 0) + 1 })}>
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="field">
          <label>Когда привезти</label>
          <input type="date" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Оплата</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="CASH">Наличными водителю</option>
            <option value="CARD_COURIER">Картой водителю</option>
            {creditAllowed ? <option value="CREDIT">В долг (по отсрочке)</option> : null}
          </select>
        </div>
        <div className="spread" style={{ fontSize: 18, margin: '10px 0' }}>
          <span>Итого</span>
          <b>{money(total)}</b>
        </div>
        <button className="primary big" style={{ width: '100%' }} disabled={busy || !items.length}>
          Заказать
        </button>
        <ErrorBox error={error} />
      </div>
    </form>
  );
}
